import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { VideoShowcase } from "../components/sections/VideoShowcase";
import { Problem } from "../components/sections/Problem";
import { Solution } from "../components/sections/Solution";
import { HowItWorks } from "../components/sections/HowItWorks";
import { Comparison } from "../components/sections/Comparison";
import { WhoWeServe } from "../components/sections/WhoWeServe";
import { Manifesto } from "../components/sections/Manifesto";
import { Impact } from "../components/sections/Impact";
import { Pilot } from "../components/sections/Pilot";
import { Timeline } from "../components/sections/Timeline";
import { Partnership } from "../components/sections/Partnership";
import { Footer } from "../components/sections/Footer";
import { Watermark } from "../components/visuals/Watermark";
import { FloatingContact } from "../components/visuals/FloatingContact";
import { ScrollProgress } from "../components/visuals/ScrollProgress";
import useReveal from "../lib/useReveal";

export default function OverdoseResponsePage() {
    useReveal();
    const location = useLocation();
    const isHome = location.pathname === "/";
    return (
        <main data-testid="overdose-response-page" className="min-h-screen bg-white">
            <ScrollProgress />
            <Navbar />

            {/* Page hero */}
            <section id="hero" className="relative bg-[#0A1428] text-white pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-32 -right-24 w-[520px] h-[520px] opacity-[0.08] pointer-events-none float-slow">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>
                <div className="absolute -bottom-40 -left-20 w-[520px] h-[520px] opacity-[0.05] pointer-events-none float-slower">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 100,40 180,160" fill="#3B82F6" />
                    </svg>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    {!isHome && (
                        <Link
                            to="/"
                            data-testid="overdose-back"
                            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-300 hover:text-white transition-colors mb-10"
                        >
                            <ArrowLeft size={16} strokeWidth={2} />
                            Back to Kairos
                        </Link>
                    )}

                    <div className="inline-flex items-center gap-3 mb-6">
                        <span className="relative inline-flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#F0493E] pulse-ring" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F0493E]" />
                        </span>
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            {isHome ? "Kairos ORD LLC · Flagship Solution" : "Flagship Solution"}
                        </span>
                    </div>

                    <h1 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-tighter text-balance max-w-5xl">
                        Meet the Kairos
                        <br />
                        Overdose Response Device.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed font-medium">
                        A rugged, connected dispenser that provides rapid
                        access to over-the-counter Naloxone and emergency
                        supplies — placed where communities and facilities
                        want them available.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/pilot-application"
                            data-testid="overdose-cta-pilot"
                            className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm"
                        >
                            Apply to the pilot
                            <ArrowRight size={18} strokeWidth={2} />
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="overdose-cta-contact"
                            className="inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm border border-white/40 text-white hover:bg-white hover:text-[#0A1428] transition-colors"
                        >
                            Contact us
                        </Link>
                    </div>
                </div>
            </section>

            <VideoShowcase />
            <Problem />
            <Solution />
            <HowItWorks />
            <Comparison />
            <WhoWeServe />
            <Manifesto />
            <Watermark text="RESPOND" theme="light" />
            <Impact />
            <Pilot />
            <Timeline />
            <Partnership />
            <Footer />
            <FloatingContact />
        </main>
    );
}
